import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Eye, KeyRound, Pencil, Plus, RotateCcw, Trash2, XCircle } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { PASSWORD_MIN } from '../../../components/auth/credentialSchema';
import AdminPageShell from '../../components/AdminPageShell';
import AdminTabs from '../../components/AdminTabs';
import AdminModal from '../../components/AdminModal';
import FieldList from '../../components/FieldList';
import AllFieldsSection from '../../components/AllFieldsSection';
import EntityAuditList from '../../components/EntityAuditList';
import StatusBadge from '../../components/StatusBadge';
import SearchSelect, { SearchOption } from '../../components/SearchSelect';
import { formatDateTime } from '../../lib/format';
import { searchActiveWorkspaces } from '../../lib/pickers';
import { startUserSimulation } from '../../lib/simulation';

interface UserDetail {
  user: Record<string, unknown> & {
    id: string;
    username: string | null;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    email_verified: boolean | null;
    is_active?: boolean;
    deactivated_at?: string | null;
    last_login_at?: string | null;
    previous_login_at?: string | null;
    created_at: string;
    updated_at: string;
  };
  memberships: Array<{
    membership_id: string;
    role: string;
    member_type: string | null;
    organization_id: string;
    organization_name: string;
    org_category: string | null;
    subscription: string | null;
    organization_is_active: boolean;
  }>;
  workspaces: Array<{
    workspace_id: string;
    workspace_name: string;
    workspace_is_active: boolean;
    role: string;
    status: string;
    organization_id: string | null;
    organization_name: string | null;
    organization_is_active: boolean | null;
    /** Reached through another org's workspace roster — not staff of the owner org. */
    outside_owner_org: boolean;
  }>;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'organizations', label: 'Organizations' },
  { key: 'workspaces', label: 'Workspaces' },
  { key: 'activity', label: 'Activity' },
];

/** Readable strong password for the manual last-resort path (never sent anywhere until the admin saves). */
const generatePassword = (): string => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
};

const AccountUserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ username: '', displayName: '', firstName: '', lastName: '', email: '' });
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [addOrgForm, setAddOrgForm] = useState({ organizationId: '', memberType: 'staff' });
  const [removeMembershipTarget, setRemoveMembershipTarget] = useState<{
    membershipId: string;
    organizationName: string;
    warning: string;
  } | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState<SearchOption | null>(null);
  const [workspaceRole, setWorkspaceRole] = useState('contributor');
  const [workspaceRoleFilter, setWorkspaceRoleFilter] = useState('');
  const [workspaceOriginFilter, setWorkspaceOriginFilter] = useState<'' | 'own' | 'outside'>('');
  const [removeWorkspaceTarget, setRemoveWorkspaceTarget] = useState<{
    workspaceId: string;
    workspaceName: string;
  } | null>(null);
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualPassword, setManualPassword] = useState('');
  const [showManualPassword, setShowManualPassword] = useState(false);

  const detail = useQuery({
    queryKey: ['admin-accounts', 'user', userId],
    queryFn: async () => {
      const response = await api.get(`/admin/accounts/users/${userId}`);
      return response.data.data as UserDetail;
    },
    enabled: !!userId,
  });

  const orgOptions = useQuery({
    queryKey: ['admin-accounts', 'active-org-options'],
    queryFn: async () => {
      const response = await api.get('/admin/accounts/organizations', {
        params: { status: 'active', limit: 100 },
      });
      return (response.data.data.organizations as Array<{ id: string; name: string }>).map(
        ({ id, name }) => ({ id, name })
      );
    },
    enabled: tab === 'organizations',
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'user', userId] });
    queryClient.invalidateQueries({ queryKey: ['admin-list'] });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'user', userId ?? ''] });
  };

  const editMutation = useMutation({
    mutationFn: () =>
      api.put(`/admin/users/${userId}`, {
        username: editForm.username || undefined,
        displayName: editForm.displayName || undefined,
        firstName: editForm.firstName || undefined,
        lastName: editForm.lastName || undefined,
        email: editForm.email || undefined,
      }),
    onSuccess: () => {
      toast.success('User updated');
      setEditOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to update user'),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => api.put(`/admin/accounts/users/${userId}/deactivate`),
    onSuccess: () => {
      toast.success('User deactivated');
      setDeactivateOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to deactivate user'),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => api.put(`/admin/accounts/users/${userId}/reactivate`),
    onSuccess: () => {
      toast.success('User reactivated');
      setReactivateOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to reactivate user'),
  });

  const closeResetModal = () => {
    setResetOpen(false);
    setResetLink(null);
    setManualOpen(false);
    setManualPassword('');
    setShowManualPassword(false);
  };

  // One endpoint, three deliveries (Documentation/ADMINTOOL_USER_PASSWORDS.md).
  const passwordResetMutation = useMutation({
    mutationFn: (payload: { deliver: 'email' | 'link' | 'manual'; password?: string }) =>
      api.post(`/admin/accounts/users/${userId}/password-reset`, payload),
    onSuccess: (response, variables) => {
      const data = response.data.data ?? {};
      if (variables.deliver === 'email') {
        toast.success(`Reset email sent to ${data.email ?? 'the user'}`);
        // A fresh token was minted — a link shown earlier is superseded.
        setResetLink(null);
      } else if (variables.deliver === 'link') {
        setResetLink(data.reset_url ?? null);
      } else {
        toast.success(
          `Password set — ${data.sessions_revoked ?? 0} session${data.sessions_revoked === 1 ? '' : 's'} signed out`
        );
        closeResetModal();
      }
      queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'user', userId ?? ''] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to reset password'),
  });

  const resetActionLoading = (deliver: string) =>
    passwordResetMutation.isLoading && passwordResetMutation.variables?.deliver === deliver;

  const copyResetLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Reset link copied');
    } catch {
      toast.error('Could not copy — select and copy the link text');
    }
  };

  const addMembershipMutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/users/${userId}/organizations`, {
        organizationId: addOrgForm.organizationId,
        role: addOrgForm.memberType === 'staff' ? 'org_admin' : undefined,
        memberType: addOrgForm.memberType,
      }),
    onSuccess: () => {
      toast.success('Added to organization');
      setAddOrgForm({ organizationId: '', memberType: 'staff' });
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to add to organization'),
  });

  const removeMembershipMutation = useMutation({
    mutationFn: (membershipId: string) => api.delete(`/admin/user-organizations/${membershipId}`),
    onSuccess: () => {
      toast.success('Removed from organization');
      setRemoveMembershipTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to remove from organization'),
  });

  const addWorkspaceMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/workspace-members', {
        workspaceId: selectedWorkspace?.id,
        userId,
        role: workspaceRole,
      }),
    onSuccess: () => {
      toast.success('Workspace access granted');
      setSelectedWorkspace(null);
      setWorkspaceRole('contributor');
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to grant workspace access'),
  });

  const removeWorkspaceMutation = useMutation({
    mutationFn: (workspaceId: string) =>
      api.delete('/admin/workspace-members', { data: { workspaceId, userId } }),
    onSuccess: () => {
      toast.success('Workspace access removed');
      setRemoveWorkspaceTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to remove workspace access'),
  });

  // Removing an org_admin membership may leave the org without any admin —
  // warn (not block) per ACCOUNTS_TOOL_REQUIREMENTS.md §2.3.3.
  const openRemoveMembership = async (membership: UserDetail['memberships'][number]) => {
    let warning = '';
    if (membership.role?.toLowerCase() === 'org_admin') {
      try {
        const response = await api.get(`/admin/accounts/organizations/${membership.organization_id}`);
        const adminCount = (response.data.data.member_counts as { org_admins: number }).org_admins;
        if (adminCount <= 1) {
          warning = ` Warning: this is the organization's last org_admin — removing it leaves "${membership.organization_name}" without an administrator.`;
        }
      } catch {
        // proceed without the warning if the lookup fails
      }
    }
    setRemoveMembershipTarget({
      membershipId: membership.membership_id,
      organizationName: membership.organization_name,
      warning,
    });
  };

  if (detail.isLoading) {
    return (
      <AdminPageShell title="User" backUrl="/admin-next/accounts?tab=users">
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading user…</p>
      </AdminPageShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AdminPageShell title="User" backUrl="/admin-next/accounts?tab=users">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">Couldn't load this user.</p>
          <Button variant="outline" size="sm" onClick={() => detail.refetch()}>
            Retry
          </Button>
        </div>
      </AdminPageShell>
    );
  }

  const { user, memberships, workspaces } = detail.data;
  const visibleWorkspaces = workspaces.filter(
    (workspace) =>
      (!workspaceRoleFilter || workspace.role?.toLowerCase() === workspaceRoleFilter) &&
      (workspaceOriginFilter === '' ||
        (workspaceOriginFilter === 'outside') === Boolean(workspace.outside_owner_org))
  );
  const name =
    user.display_name || [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || user.email || user.id;
  const isActive = user.is_active !== false;
  const targetIsSystemAdmin = memberships.some((membership) => membership.role?.toLowerCase() === 'system_admin');

  const openEdit = () => {
    setEditForm({
      username: user.username ?? '',
      displayName: user.display_name ?? '',
      firstName: user.first_name ?? '',
      lastName: user.last_name ?? '',
      email: user.email ?? '',
    });
    setEditOpen(true);
  };

  return (
    <AdminPageShell
      title={String(name)}
      description="User"
      backUrl="/admin-next/accounts?tab=users"
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            icon={Eye}
            disabled={targetIsSystemAdmin || !isActive}
            title={
              targetIsSystemAdmin
                ? 'System administrators cannot be simulated'
                : !isActive
                  ? 'Deactivated users cannot be simulated'
                  : undefined
            }
            onClick={() => setSimulateOpen(true)}
          >
            Simulate
          </Button>
          <Button variant="outline" size="sm" icon={Pencil} onClick={openEdit}>
            Edit
          </Button>
          <Button variant="outline" size="sm" icon={KeyRound} onClick={() => setResetOpen(true)}>
            Reset Password
          </Button>
          {isActive ? (
            <Button
              variant="outline"
              size="sm"
              icon={XCircle}
              disabled={targetIsSystemAdmin}
              title={targetIsSystemAdmin ? 'System administrators cannot be deactivated' : undefined}
              onClick={() => setDeactivateOpen(true)}
            >
              Deactivate
            </Button>
          ) : (
            <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setReactivateOpen(true)}>
              Reactivate
            </Button>
          )}
        </>
      }
    >
      <div className="flex items-center gap-2">
        <StatusBadge active={isActive} />
        {user.deactivated_at && !isActive && (
          <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
            since {formatDateTime(user.deactivated_at as string)}
          </span>
        )}
      </div>

      <AdminTabs tabs={TABS} activeKey={tab} onChange={setTab} />

      {tab === 'overview' && (
        <div className="card space-y-4 p-5">
          <FieldList
            fields={[
              { label: 'ID', value: <span className="font-mono text-xs">{user.id}</span>, copyValue: user.id },
              { label: 'Username', value: user.username ?? '—' },
              { label: 'Display Name', value: user.display_name ?? '—' },
              {
                label: 'Email',
                value: user.email ? `${user.email}${user.email_verified ? ' (verified)' : ''}` : '—',
              },
              { label: 'Status', value: <StatusBadge active={isActive} /> },
              { label: 'Last Login', value: formatDateTime(user.last_login_at as string | null) },
              { label: 'Previous Login', value: formatDateTime(user.previous_login_at as string | null) },
              { label: 'Created', value: formatDateTime(user.created_at) },
              { label: 'Updated', value: formatDateTime(user.updated_at) },
              { label: 'Deactivated At', value: formatDateTime(user.deactivated_at as string | null) },
            ]}
          />
          <AllFieldsSection record={user} />
        </div>
      )}

      {tab === 'organizations' && (
        <div className="space-y-4">
          <div className="card p-4">
            <h4 className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              Add to organization
            </h4>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={addOrgForm.organizationId}
                onChange={(event) => setAddOrgForm((current) => ({ ...current, organizationId: event.target.value }))}
                aria-label="Organization"
                className="h-9 min-w-[14rem] rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              >
                <option value="">Select an active organization…</option>
                {(orgOptions.data ?? []).map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              <select
                value={addOrgForm.memberType}
                onChange={(event) => setAddOrgForm((current) => ({ ...current, memberType: event.target.value }))}
                aria-label="Membership type"
                className="h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              >
                <option value="staff">Staff (org_admin)</option>
                <option value="citizen">Citizen affiliation</option>
              </select>
              <Button
                variant="primary"
                size="sm"
                icon={Plus}
                disabled={addOrgForm.organizationId === ''}
                loading={addMembershipMutation.isLoading}
                onClick={() => addMembershipMutation.mutate()}
              >
                Add
              </Button>
            </div>
          </div>

          <div className="card p-5">
            {memberships.length === 0 ? (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No organization memberships.</p>
            ) : (
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {memberships.map((membership) => (
                  <li key={membership.membership_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <Link
                      to={`/admin-next/accounts/organizations/${membership.organization_id}`}
                      className="font-medium text-primary-700 hover:underline dark:text-primary-400"
                    >
                      {membership.organization_name}
                    </Link>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-background-secondary px-2 py-0.5 text-xs text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
                        {membership.role}
                      </span>
                      {membership.member_type && (
                        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{membership.member_type}</span>
                      )}
                      <StatusBadge active={membership.organization_is_active} />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        onClick={() => openRemoveMembership(membership)}
                        ariaLabel={`Remove membership in ${membership.organization_name}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === 'workspaces' && (
        <div className="space-y-4">
          <div className="card p-4">
            <h4 className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              Grant workspace access
            </h4>
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[16rem] flex-1">
                <SearchSelect
                  placeholder="Search active workspaces…"
                  fetchOptions={searchActiveWorkspaces}
                  selected={selectedWorkspace}
                  onSelect={setSelectedWorkspace}
                  onClear={() => setSelectedWorkspace(null)}
                  ariaLabel="Search workspaces"
                />
              </div>
              <select
                value={workspaceRole}
                onChange={(event) => setWorkspaceRole(event.target.value)}
                aria-label="Workspace role"
                className="h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              >
                <option value="owner">Owner</option>
                <option value="contributor">Contributor</option>
                <option value="guest">Guest</option>
              </select>
              <Button
                variant="primary"
                size="sm"
                icon={Plus}
                disabled={!selectedWorkspace}
                loading={addWorkspaceMutation.isLoading}
                onClick={() => addWorkspaceMutation.mutate()}
              >
                Grant
              </Button>
            </div>
          </div>

          <div className="card p-5">
          {workspaces.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <select
                value={workspaceRoleFilter}
                onChange={(event) => setWorkspaceRoleFilter(event.target.value)}
                aria-label="Filter workspaces by role"
                className="h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              >
                <option value="">All roles</option>
                <option value="owner">Owner</option>
                <option value="contributor">Contributor</option>
                <option value="guest">Guest</option>
              </select>
              <select
                value={workspaceOriginFilter}
                onChange={(event) => setWorkspaceOriginFilter(event.target.value as '' | 'own' | 'outside')}
                aria-label="Filter workspaces by owner organization"
                className="h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              >
                <option value="">All organizations</option>
                <option value="own">Their organizations</option>
                <option value="outside">Other organizations</option>
              </select>
            </div>
          )}
          {workspaces.length === 0 ? (
            <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No workspace access.</p>
          ) : visibleWorkspaces.length === 0 ? (
            <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No workspaces match these filters.</p>
          ) : (
            <ul className="divide-y divide-border-light dark:divide-border-dark">
              {visibleWorkspaces.map((workspace) => (
                <li key={workspace.workspace_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <div>
                    <Link
                      to={`/admin-next/accounts/workspaces/${workspace.workspace_id}`}
                      className="font-medium text-primary-700 hover:underline dark:text-primary-400"
                    >
                      {workspace.workspace_name}
                    </Link>
                    {workspace.organization_name && (
                      <span className="ml-2 text-text-tertiary dark:text-text-dark-tertiary">
                        {workspace.organization_name}
                      </span>
                    )}
                    {workspace.outside_owner_org && (
                      <span
                        className="ml-2 inline-flex rounded-full bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700 dark:bg-warning-900/30 dark:text-warning-300"
                        title="Access comes from this workspace's roster; they are not staff of the organization that owns it."
                      >
                        Outside org
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-background-secondary px-2 py-0.5 text-xs text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
                      {workspace.role}
                    </span>
                    <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{workspace.status}</span>
                    <StatusBadge
                      active={workspace.workspace_is_active}
                      viaParent={workspace.workspace_is_active && workspace.organization_is_active === false}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Trash2}
                      onClick={() =>
                        setRemoveWorkspaceTarget({
                          workspaceId: workspace.workspace_id,
                          workspaceName: workspace.workspace_name,
                        })
                      }
                      ariaLabel={`Remove access to ${workspace.workspace_name}`}
                    >
                      Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          </div>
        </div>
      )}

      {tab === 'activity' && userId && <EntityAuditList entityType="user" entityId={userId} />}

      <AdminModal
        isOpen={editOpen}
        title="Edit User"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={editMutation.isLoading} onClick={() => editMutation.mutate()}>
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {(
            [
              { key: 'displayName', label: 'Display Name', type: 'text' },
              { key: 'firstName', label: 'First Name', type: 'text' },
              { key: 'lastName', label: 'Last Name', type: 'text' },
              { key: 'username', label: 'Username', type: 'text' },
              { key: 'email', label: 'Email', type: 'email' },
            ] as const
          ).map((field) => (
            <div key={field.key}>
              <label htmlFor={`user-${field.key}`} className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                {field.label}
              </label>
              <input
                id={`user-${field.key}`}
                type={field.type}
                value={editForm[field.key]}
                onChange={(event) => setEditForm((current) => ({ ...current, [field.key]: event.target.value }))}
                className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              />
            </div>
          ))}
        </div>
      </AdminModal>

      <AdminModal
        isOpen={resetOpen}
        title="Reset Password"
        onClose={closeResetModal}
        footer={
          <Button variant="secondary" size="sm" onClick={closeResetModal}>
            Close
          </Button>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            The user sets their own password from a reset link (valid 24 hours, single use).
            Issuing a new link replaces any outstanding one; completing a reset signs the user
            out everywhere.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!user.email}
              title={user.email ? undefined : 'This user has no primary email'}
              loading={resetActionLoading('email')}
              onClick={() => passwordResetMutation.mutate({ deliver: 'email' })}
            >
              Send reset email{user.email ? ` to ${user.email}` : ''}
            </Button>
            <Button
              variant="outline"
              size="sm"
              loading={resetActionLoading('link')}
              onClick={() => passwordResetMutation.mutate({ deliver: 'link' })}
            >
              Get reset link
            </Button>
          </div>

          {resetLink && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 rounded-lg border border-border-light bg-background-secondary p-2 dark:border-border-dark dark:bg-background-dark-tertiary">
                <code className="min-w-0 flex-1 break-all text-xs text-text-primary dark:text-text-dark-primary">
                  {resetLink}
                </code>
                <Button variant="outline" size="sm" onClick={() => copyResetLink(resetLink)}>
                  Copy
                </Button>
              </div>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Shown once — deliver it to the user through a channel you trust.
              </p>
            </div>
          )}

          <div>
            <button
              type="button"
              className="text-xs font-medium text-text-tertiary underline hover:text-text-secondary dark:text-text-dark-tertiary dark:hover:text-text-dark-secondary"
              onClick={() => setManualOpen((current) => !current)}
            >
              {manualOpen ? 'Hide manual option' : 'Set password manually (last resort)'}
            </button>
            {manualOpen && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  {/* 'new-password' stops browsers autofilling the admin's own
                      saved credentials (autocomplete="off" is ignored here) */}
                  <input
                    type={showManualPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={manualPassword}
                    aria-label="New password"
                    placeholder={`Min ${PASSWORD_MIN} characters`}
                    onChange={(event) => setManualPassword(event.target.value)}
                    className="w-full flex-1 rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
                  />
                  <Button variant="outline" size="sm"
                    onClick={() => { setManualPassword(generatePassword()); setShowManualPassword(true); }}>
                    Generate
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowManualPassword((current) => !current)}>
                    {showManualPassword ? 'Hide' : 'Show'}
                  </Button>
                </div>
                <div className="flex items-center justify-end">
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={manualPassword.length < PASSWORD_MIN}
                    loading={resetActionLoading('manual')}
                    onClick={() => passwordResetMutation.mutate({ deliver: 'manual', password: manualPassword })}
                  >
                    Set Password Now
                  </Button>
                </div>
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  Sets the password immediately, signs the user out everywhere, and voids any
                  outstanding reset link. You'll know this credential — tell the user to change
                  it after signing in.
                </p>
              </div>
            )}
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={deactivateOpen}
        title="Deactivate user"
        message={`Deactivate ${name}? They will be signed out and unable to log in until reactivated. Their organization memberships and workspace access are preserved. Nothing is deleted.`}
        confirmText="Deactivate"
        variant="warning"
        onConfirm={() => deactivateMutation.mutate()}
        onCancel={() => setDeactivateOpen(false)}
      />

      <ConfirmationDialog
        isOpen={reactivateOpen}
        title="Reactivate user"
        message={`Reactivate ${name}? They regain login access with all previous memberships and roles.`}
        confirmText="Reactivate"
        variant="info"
        onConfirm={() => reactivateMutation.mutate()}
        onCancel={() => setReactivateOpen(false)}
      />

      <ConfirmationDialog
        isOpen={removeMembershipTarget !== null}
        title="Remove organization membership"
        message={
          removeMembershipTarget
            ? `Remove ${name}'s membership in "${removeMembershipTarget.organizationName}"?${removeMembershipTarget.warning}`
            : ''
        }
        confirmText="Remove"
        variant={removeMembershipTarget?.warning ? 'danger' : 'warning'}
        onConfirm={() => removeMembershipTarget && removeMembershipMutation.mutate(removeMembershipTarget.membershipId)}
        onCancel={() => setRemoveMembershipTarget(null)}
      />

      <ConfirmationDialog
        isOpen={removeWorkspaceTarget !== null}
        title="Remove workspace access"
        message={
          removeWorkspaceTarget
            ? `Remove ${name}'s access to "${removeWorkspaceTarget.workspaceName}"? The membership is archived, not deleted.`
            : ''
        }
        confirmText="Remove"
        variant="warning"
        onConfirm={() => removeWorkspaceTarget && removeWorkspaceMutation.mutate(removeWorkspaceTarget.workspaceId)}
        onCancel={() => setRemoveWorkspaceTarget(null)}
      />

      <ConfirmationDialog
        isOpen={simulateOpen}
        title="Simulate user"
        message={`View the application as ${name} (${user.email ?? 'no email'})? You'll see exactly what they see, with their permissions. An amber banner stays on screen until you exit the simulation, and the session is logged. Sessions expire after 2 hours.`}
        confirmText={simulating ? 'Starting…' : 'Start Simulation'}
        variant="info"
        onConfirm={async () => {
          if (simulating || !userId) return;
          setSimulating(true);
          try {
            await startUserSimulation(userId, `/admin-next/accounts/users/${userId}`);
          } catch (error: any) {
            toast.error(error?.response?.data?.error || 'Failed to start simulation');
            setSimulating(false);
          }
        }}
        onCancel={() => setSimulateOpen(false)}
      />
    </AdminPageShell>
  );
};

export default AccountUserDetailPage;
